import { useState, useEffect } from "react";
import { RotateCcw, Undo2 } from "lucide-react";
import {
  I,
  DEFAULT_WEEKLY_SCHEDULE,
  normalizeWeeklySchedule,
  weeklyHours,
  weeklyBreakdown,
  slotToTime,
} from "../utils";
import { LockButton } from "../components/SharedUI";
import { useTaxonomyList } from "../hooks/useTaxonomy";
import { getWorkLocationMeta, workLocationCycle } from "../lib/taxonomyHelpers";
import { useTranslation } from "../i18n";
import { defaultScheduleAxis, type AxisDay } from "../lib/scheduleAxis";
import { CT, RAD, FS, Z } from "../lib/tokens";

const SLOT_H = 18;

// ── Module-level state: survives React remounts, no stale closures ──
const _persist: Record<string, any> = {};
const gp = (id: any, key: any, def: any) => {
  const k = id + ":" + key;
  return _persist[k] !== undefined ? _persist[k] : def;
};
const sp = (id: any, key: any, val: any) => {
  _persist[id + ":" + key] = val;
};

let _painting = false;
let _paintDrag: any = null; // { dayIdx, startSlot, currentSlot, mouseX, mouseY }
let _onChangeLatest: any = null;
let _wsLatest: any = null;
let _paintModeLatest = "onsite";
let _rerenderFn: any = null;

// Save & restore scroll of nearest scrollable ancestor so re-renders don't jump
function saveScroll() {
  const el = document.querySelector(".content");
  return el ? el.scrollTop : 0;
}
function restoreScroll(pos: any) {
  requestAnimationFrame(() => {
    const el = document.querySelector(".content");
    if (el) el.scrollTop = pos;
  });
}

function paintSlot(dayIdx: any, slotIdx: any) {
  const ws = _wsLatest;
  const mode = _paintModeLatest;
  if (!ws) return;
  const day = ws[dayIdx];
  if (!day) return;
  const slots = [...(day.slots || [])];
  const val = mode === "eraser" ? null : mode;
  if (slots[slotIdx] === val) return;
  slots[slotIdx] = val;
  const nws = ws.map((d: any, i: any) => (i === dayIdx ? { ...d, slots } : d));
  _wsLatest = nws;
  const scrollPos = saveScroll();
  if (_onChangeLatest) _onChangeLatest(nws);
  restoreScroll(scrollPos);
}

function handleCellDown(dayIdx: any, slotIdx: any, e: any) {
  e.preventDefault();
  e.stopPropagation();
  _painting = true;
  _paintDrag = {
    dayIdx,
    startSlot: slotIdx,
    currentSlot: slotIdx,
    mouseX: e.clientX,
    mouseY: e.clientY,
  };
  paintSlot(dayIdx, slotIdx);
  if (_rerenderFn) _rerenderFn();
}

function handleCellMove(dayIdx: any, slotIdx: any, e: any) {
  if (!_painting) return;
  e.preventDefault();
  e.stopPropagation();
  if (!_paintDrag || _paintDrag.dayIdx !== dayIdx) return;
  _paintDrag.mouseX = e.clientX;
  _paintDrag.mouseY = e.clientY;
  if (slotIdx !== _paintDrag.currentSlot) {
    _paintDrag.currentSlot = slotIdx;
    paintSlot(dayIdx, slotIdx);
    if (_rerenderFn) _rerenderFn();
  }
}

function handleMouseUp() {
  if (_painting) {
    _painting = false;
    _paintDrag = null;
    if (_rerenderFn) _rerenderFn();
  }
}

// ── Component ──
const ScheduleGrid = ({
  schedule,
  onChange,
  otherSchedules,
  readOnly,
  canUnlock,
  scheduleId,
  schedAxis,
}: any) => {
  const { t, locale } = useTranslation();
  const _dayShortFmt = new Intl.DateTimeFormat(locale || "en-GB", {
    weekday: "short",
  });
  const _localizedDayShort = (idx0to4: any) => {
    const d = new Date(2024, 0, 1 + idx0to4);
    return _dayShortFmt.format(d);
  };
  const _workLocList = useTaxonomyList("work_location");
  const _LOC_CLR = (code: any) => getWorkLocationMeta(code, _workLocList).color;
  const _LOC_LBL = (code: any) => getWorkLocationMeta(code, _workLocList).label;
  const _LOC_CYCLE = workLocationCycle(_workLocList);
  // 58 §5b — the grid draws the FIRM's axis: its days, its hours. `schedAxis`
  // is optional so a caller that has not been threaded yet still renders
  // today's constants rather than nothing.
  const axis = schedAxis || defaultScheduleAxis();
  const ws = normalizeWeeklySchedule(schedule, axis.slots);
  const lid = scheduleId || "default";
  console.log(
    "ScheduleGrid props:",
    JSON.stringify({
      locked: _persist[lid + ":locked"],
      paintMode: _persist[lid + ":mode"] || "onsite",
      scheduleId: lid,
      readOnly,
      canUnlock,
      hasSchedule: !!schedule,
      hasOnChange: typeof onChange === "function",
    }),
  );
  const [tick, setTick] = useState(0);
  const rerender = () => setTick((t) => t + 1);

  // Keep module-level refs in sync
  _wsLatest = ws;
  _onChangeLatest = onChange;
  _rerenderFn = rerender;
  _paintModeLatest = gp(lid, "mode", "onsite");

  const locked = gp(lid, "locked", true);
  const paintMode = gp(lid, "mode", "onsite");

  const setLocked = (v: any) => {
    const nv = typeof v === "function" ? v(locked) : v;
    if (!nv && locked) {
      // Unlocking: snapshot current schedule for undo
      sp(lid, "undo", JSON.stringify(ws));
    }
    sp(lid, "locked", nv);
    rerender();
  };
  const setPaintMode = (m: any) => {
    sp(lid, "mode", m);
    rerender();
  };

  const doUndo = () => {
    const scrollPos = saveScroll();
    const snap = gp(lid, "undo", null);
    if (snap) {
      try {
        onChange(JSON.parse(snap));
      } catch {}
    }
    sp(lid, "locked", true);
    rerender();
    restoreScroll(scrollPos);
  };

  const doReset = () => {
    const scrollPos = saveScroll();
    onChange(DEFAULT_WEEKLY_SCHEDULE);
    rerender();
    restoreScroll(scrollPos);
  };

  const editing = !readOnly && canUnlock !== false && !locked;

  // Window-level mouseup — always listening, cleanup on unmount
  useEffect(() => {
    window.addEventListener("mouseup", handleMouseUp, true);
    return () => window.removeEventListener("mouseup", handleMouseUp, true);
  }, []);

  const getSlot = (dayIdx: any, slotIdx: any) =>
    (ws[dayIdx]?.slots || [])[slotIdx] || null;

  const hours = [];
  for (let i = 0; i < axis.slots; i++) {
    if (i % 2 === 0) hours.push({ idx: i, label: `${axis.start + i / 2}:00` });
  }

  const dayHours = (dayIdx: any) =>
    (ws[dayIdx]?.slots || []).filter(Boolean).length * 0.5;
  const tot = weeklyHours(ws);
  const bd = weeklyBreakdown(ws);

  // Drag tooltip range
  const dragRange =
    _painting && _paintDrag
      ? (() => {
          const lo = Math.min(_paintDrag.startSlot, _paintDrag.currentSlot);
          const hi = Math.max(_paintDrag.startSlot, _paintDrag.currentSlot);
          return slotToTime(lo) + " – " + slotToTime(hi + 1);
        })()
      : null;

  const modeBtn = (mode: any, label: any, color: any) => {
    const sel = paintMode === mode;
    return (
      <button
        type="button"
        key={mode}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          padding: "4px 10px",
          fontSize: FS.s10,
          fontWeight: 600,
          fontFamily: "inherit",
          borderRadius: RAD.r6,
          cursor: "pointer",
          background: sel ? color : "var(--bc)",
          color: sel ? "var(--tp)" : "var(--ts)",
          border: `2px solid ${sel ? color : "var(--br)"}`,
          opacity: sel ? 1 : 0.7,
          transition: "all .12s",
        }}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setPaintMode(mode);
        }}
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
      >
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: RAD.r2,
            background: color,
            display: "inline-block",
            border: "1px solid var(--br)",
          }}
        />
        {label}
      </button>
    );
  };

  return (
    <div
      style={{
        userSelect: _painting ? "none" : undefined,
        position: "relative",
      }}
    >
      <div
        style={{
          display: "flex",
          gap: 6,
          marginBottom: 10,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        {editing && (
          <>
            {modeBtn(
              "onsite",
              t("teamSchedule.onsite", { fallback: "Onsite" }),
              _LOC_CLR("onsite"),
            )}
            {modeBtn(
              "thirdparty",
              t("teamSchedule.thirdparty", { fallback: "Third Party" }),
              _LOC_CLR("thirdparty"),
            )}
            {modeBtn(
              "home",
              t("teamSchedule.home", { fallback: "Home" }),
              _LOC_CLR("home"),
            )}
            {modeBtn(
              "eraser",
              t("teamSchedule.eraser", { fallback: "Eraser" }),
              CT.c_666,
            )}
            <span
              style={{
                width: 1,
                height: 18,
                background: "var(--br)",
                margin: "0 2px",
                flexShrink: 0,
              }}
            />
            <button
              type="button"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                padding: "4px 10px",
                fontSize: FS.s10,
                fontWeight: 600,
                fontFamily: "inherit",
                borderRadius: RAD.r6,
                cursor: "pointer",
                background: "color-mix(in srgb, var(--tp) 6%, transparent)",
                color: "var(--ts)",
                border: "1px solid var(--br)",
                opacity: 0.8,
              }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                doReset();
              }}
              onMouseDown={(e) => e.stopPropagation()}
              title={t("schedule.reset_default_title", {
                fallback: "Reset to default 9-12:30 / 13:30-17 onsite",
              })}
            >
              <RotateCcw size={11} />
              {t("schedule.reset", { fallback: "Reset" })}
            </button>
            <button
              type="button"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                padding: "4px 10px",
                fontSize: FS.s10,
                fontWeight: 600,
                fontFamily: "inherit",
                borderRadius: RAD.r6,
                cursor: "pointer",
                background: "color-mix(in srgb, var(--tp) 6%, transparent)",
                color: "var(--ts)",
                border: "1px solid var(--br)",
                opacity: 0.8,
              }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                doUndo();
              }}
              onMouseDown={(e) => e.stopPropagation()}
              title={t("schedule.undo_lock_title", {
                fallback: "Undo all changes and lock",
              })}
            >
              <Undo2 size={11} />
              {t("common.undo", { fallback: "Undo" })}
            </button>
          </>
        )}
        {!editing &&
          _LOC_CYCLE.map((loc) => (
            <span
              key={loc}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                fontSize: FS.s10,
              }}
            >
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: RAD.r2,
                  background: _LOC_CLR(loc),
                  display: "inline-block",
                }}
              />
              <span style={{ color: "var(--ts)" }}>
                {t("teamSchedule." + loc, { fallback: _LOC_LBL(loc) })}
              </span>
            </span>
          ))}
        {canUnlock !== false && !readOnly && (
          <LockButton
            {...({
              locked: locked,
              onToggle: () => setLocked((v: any) => !v),
              style: { marginLeft: "auto" },
            } as any)}
          />
        )}
      </div>

      {/* Floating tooltip following cursor during drag */}
      {dragRange && _paintDrag && (
        <div
          style={{
            position: "fixed",
            left: _paintDrag.mouseX + 14,
            top: _paintDrag.mouseY - 10,
            background: "var(--bc)",
            border: "1px solid var(--br)",
            borderRadius: RAD.r6,
            padding: "3px 8px",
            fontSize: FS.s10,
            fontWeight: 600,
            fontFamily: "'JetBrains Mono',monospace",
            color:
              paintMode === "eraser"
                ? "var(--tm)"
                : _LOC_CLR(paintMode) || "var(--ac)",
            zIndex: Z.topMenu,
            pointerEvents: "none",
            whiteSpace: "nowrap",
          }}
        >
          {dragRange}
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: `36px repeat(${axis.days.length}, 1fr)`,
          gridTemplateRows: `auto repeat(${axis.slots}, ${SLOT_H}px) auto`,
        }}
      >
        {/* Row 1: header — time label column + one header per axis day */}
        <div />
        {axis.days.map((day: AxisDay, dayIdx: number) => (
          <div
            key={"h-" + day.code}
            style={{
              textAlign: "center",
              fontSize: FS.s10,
              fontWeight: 600,
              color: "var(--tp)",
              padding: "4px 0",
              borderBottom: "1px solid var(--br)",
            }}
          >
            {_localizedDayShort(dayIdx)}
          </div>
        ))}

        {/* Rows 2..axis.slots+1: slot rows */}
        {Array.from({ length: axis.slots }, (_, slotIdx) => {
          const isHour = slotIdx % 2 === 0;
          return [
            /* Time label cell */
            <div
              key={"t-" + slotIdx}
              style={{
                fontSize: FS.s8,
                color: "var(--tm)",
                fontFamily: "'JetBrains Mono',monospace",
                textAlign: "right",
                paddingRight: 4,
                lineHeight: SLOT_H + "px",
                gridRow: slotIdx + 2,
                gridColumn: 1,
              }}
            >
              {isHour ? `${axis.start + slotIdx / 2}:00` : ""}
            </div>,
            /* one cell per axis day for this slot */
            ...axis.days.map((day: AxisDay, dayIdx: number) => {
              const daySlots = ws[dayIdx]?.slots || [];
              const otherCols = (otherSchedules || []).map(
                (os: any) => os.schedule?.[dayIdx]?.slots || [],
              );
              const loc = daySlots[slotIdx] || null;
              const prev = slotIdx > 0 ? daySlots[slotIdx - 1] || null : null;
              const next =
                slotIdx < axis.slots - 1
                  ? daySlots[slotIdx + 1] || null
                  : null;
              const isFirst = loc && loc !== prev;
              const isLast = loc && loc !== next;
              const isOnly = isFirst && isLast;
              const br = isOnly
                ? 6
                : isFirst
                  ? "6px 6px 0 0"
                  : isLast
                    ? "0 0 6px 6px"
                    : 0;
              const hasOther = otherCols.some((os: any) => os[slotIdx]);
              const borderT =
                loc && prev === loc
                  ? "none"
                  : isHour
                    ? "1px solid color-mix(in srgb, var(--br) 80%, transparent)"
                    : "1px solid color-mix(in srgb, var(--br) 35%, transparent)";
              // Compute block end for time label on first slot of block
              let blockLabel = null;
              if (isFirst) {
                let endIdx = slotIdx;
                while (endIdx < axis.slots - 1 && daySlots[endIdx + 1] === loc)
                  endIdx++;
                const blockLen = endIdx - slotIdx + 1;
                if (blockLen >= 2)
                  blockLabel =
                    slotToTime(slotIdx) + "–" + slotToTime(endIdx + 1);
              }
              return (
                <div
                  key={"s-" + dayIdx + "-" + slotIdx}
                  style={{
                    gridRow: slotIdx + 2,
                    gridColumn: dayIdx + 2,
                    height: SLOT_H,
                    borderTop: loc ? "none" : borderT,
                    borderRight: "1px solid var(--br)",
                    borderLeft:
                      dayIdx === 0 ? "1px solid var(--br)" : undefined,
                    padding: "0 2px",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      background: loc
                        ? _LOC_CLR(loc)
                        : hasOther
                          ? "color-mix(in srgb, var(--tp) 4%, transparent)"
                          : "transparent",
                      borderRadius: br,
                      opacity: loc ? 1 : hasOther ? 0.3 : 1,
                      cursor: editing ? "crosshair" : "default",
                      position: "relative",
                      overflow: "visible",
                    }}
                    onMouseDown={
                      editing
                        ? (e) => handleCellDown(dayIdx, slotIdx, e)
                        : undefined
                    }
                    onMouseMove={
                      editing
                        ? (e) => handleCellMove(dayIdx, slotIdx, e)
                        : undefined
                    }
                    title={
                      loc
                        ? slotToTime(slotIdx) + " " + _LOC_LBL(loc)
                        : slotToTime(slotIdx)
                    }
                  >
                    {blockLabel && (
                      <span
                        style={{
                          position: "absolute",
                          top: 3,
                          left: 4,
                          fontSize: FS.s8,
                          fontFamily: "'JetBrains Mono',monospace",
                          color: "rgba(0,0,0,.45)",
                          lineHeight: 1,
                          pointerEvents: "none",
                          whiteSpace: "nowrap",
                          zIndex: Z.stickyHead,
                        }}
                      >
                        {blockLabel}
                      </span>
                    )}
                    {hasOther && !loc && (
                      <div
                        style={{
                          position: "absolute",
                          inset: 0,
                          display: "flex",
                          pointerEvents: "none",
                          borderRadius: br || 0,
                        }}
                      >
                        {otherCols.map((os: any, oi: any) =>
                          os[slotIdx] ? (
                            <div
                              key={oi}
                              style={{
                                flex: 1,
                                background: _LOC_CLR(os[slotIdx]),
                                opacity: 0.3,
                              }}
                            />
                          ) : null,
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            }),
          ];
        })}

        {/* Last row: per-day totals */}
        <div />
        {axis.days.map((day: AxisDay, dayIdx: number) => (
          <div
            key={"tot-" + day}
            style={{
              textAlign: "center",
              fontSize: FS.s9,
              fontWeight: 600,
              color: "var(--ts)",
              padding: "4px 0",
              fontFamily: "'JetBrains Mono',monospace",
            }}
          >
            {dayHours(dayIdx)}h
          </div>
        ))}
      </div>

      <div
        style={{
          fontSize: FS.s10,
          color: "var(--ts)",
          marginTop: 6,
          fontFamily: "'JetBrains Mono',monospace",
          lineHeight: 1.8,
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <span style={{ color: _LOC_CLR("onsite") }}>
          {t("teamSchedule.onsite", { fallback: "Onsite" })}: {bd.onsite}h
        </span>
        <span style={{ color: _LOC_CLR("thirdparty") }}>
          {t("teamSchedule.thirdparty", { fallback: "Third Party" })}:{" "}
          {bd.thirdparty}h
        </span>
        <span style={{ color: _LOC_CLR("home") }}>
          {t("teamSchedule.home", { fallback: "Home" })}: {bd.home}h
        </span>
        <span style={{ fontWeight: 700, color: "var(--tp)" }}>
          {t("common.total", { fallback: "Total" })}: {tot}h/
          {t("schedule.week_unit", { fallback: "week" })}
        </span>
      </div>
    </div>
  );
};

export default ScheduleGrid;
